<template>
    <div class="page home-page py-lg-4">
        <transition name="fade-transition">
            <v-container v-if="!loading && homeData.featured_courses !== null" :class="{'pa-0': phoneOnly}">

                <!-- Header Slider -->
                <div
                    v-if="headerImages && headerImages.length"
                    ref="bannerCarouselWrap"
                    class="header-carousel-wrap mb-2 mb-lg-6"
                >
                    <v-carousel
                        :show-arrows-on-hover="headerImages.length > 1"
                        cycle
                        :height="bannerCarouselHeight"
                        :show-arrows="headerImages.length > 1 && mdAndUp"
                        :continuous="true"
                        class="header-carousel"
                        :hide-delimiters="headerImages.length <= 1"
                    >
                        <template v-slot:prev="{ on, attrs }">
                            <v-btn large fab v-bind="attrs" v-on="on" class="transparent" depressed>
                                <v-icon large>mdi-chevron-right</v-icon>
                            </v-btn>
                        </template>
                        <template v-slot:next="{ on, attrs }">
                            <v-btn large fab v-bind="attrs" v-on="on" class="transparent" depressed>
                                <v-icon large>mdi-chevron-left</v-icon>
                            </v-btn>
                        </template>
                        <v-carousel-item
                            v-for="(img, i) in headerImages"
                            :key="i"
                            @click="imageClickAction(img)"
                            v-ripple="!!img.href && phoneOnly"
                            :class="{'e-link': !!img.href || !!img.to}"
                        >
                            <div
                                class="header-carousel__slide"
                                :style="{ backgroundImage: `url(${img.url})` }"
                                role="img"
                                :aria-label="img.alt || `banner-${i + 1}`"
                            />
                        </v-carousel-item>
                    </v-carousel>
                </div>

                <v-fade-transition>
                <v-card
                    v-if="showHomeHonorPanel && (rankedHonorPreview.length || honorPanelLoading)"
                    flat
                    class="section-card honor-section mb-4 mb-lg-10"
                >
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between align-center pt-0 pt-lg-4 flex-wrap" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>
                            <div>لوحة الشرف</div>
                            <div v-if="homeHonorExamName" class="details-text text--text font-weight-medium mt-1" style="font-size: 0.95rem">
                                امتحان: {{ homeHonorExamName }}
                            </div>
                        </div>
                        <v-btn
                            v-if="showHonorPanelViewAll"
                            text
                            color="primary"
                            class="px-2"
                            :to="{ name: 'honor-panel' }"
                        >
                            عرض الكل
                            <v-icon small class="ms-1">mdi-chevron-left</v-icon>
                        </v-btn>
                    </v-card-title>
                    <div v-if="phoneOnly" class="honor-section__mobile-head px-4 pt-3 pb-1">
                        <div class="d-flex align-center justify-space-between">
                            <div class="honor-section__subheader strong-text--text">لوحة الشرف</div>
                            <v-btn
                                v-if="showHonorPanelViewAll"
                                text
                                x-small
                                color="primary"
                                class="px-1"
                                :to="{ name: 'honor-panel' }"
                            >
                                عرض الكل
                            </v-btn>
                            <div v-else class="honor-section__hint muted--text">اسحب للمزيد ←</div>
                        </div>
                        <div v-if="homeHonorExamName" class="details-text text--text mt-1">
                            امتحان: {{ homeHonorExamName }}
                        </div>
                    </div>
                    <v-card-text class="honor-section__body pt-0 pb-3 py-lg-4 px-0 px-lg-4">
                        <div v-if="honorPanelLoading && !rankedHonorPreview.length" class="px-4 py-6 text-center">
                            <v-progress-circular indeterminate color="primary" size="28" width="3" />
                        </div>
                        <template v-else-if="rankedHonorPreview.length">
                            <!-- Mobile / app: native horizontal scroll -->
                            <div v-if="phoneOnly" class="honor-scroll-outer">
                                <div class="honor-scroll">
                                    <div
                                        v-for="student in rankedHonorPreview"
                                        :key="student.id + '-' + student.rank"
                                        class="honor-carousel__item"
                                    >
                                        <div class="honor-carousel__rank" :class="honorRankClass(student.rank)">
                                            {{ student.rank }}
                                        </div>
                                        <v-avatar size="62" class="honor-carousel__avatar">
                                            <img :src="student.avatar" :alt="student.name" />
                                        </v-avatar>
                                        <div class="honor-carousel__mark">
                                            <v-chip x-small color="primary" class="font-weight-bold px-2">
                                                {{ student.earnedMark }}/{{ student.totalMark }}
                                            </v-chip>
                                        </div>
                                        <div class="honor-carousel__meta text-center">
                                            <div class="honor-carousel__name font-weight-bold strong-text--text">
                                                {{ student.name }}
                                            </div>
                                            <div
                                                v-if="student.governorate"
                                                class="honor-carousel__gov details-text text--text"
                                            >
                                                {{ student.governorate }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Desktop: Vuetify slide group with arrows -->
                            <v-slide-group
                                v-else
                                :show-arrows="rankedHonorPreview.length > 4"
                                class="honor-carousel"
                            >
                                <v-slide-item
                                    v-for="student in rankedHonorPreview"
                                    :key="student.id + '-' + student.rank"
                                >
                                    <div class="honor-carousel__item honor-carousel__item--desktop">
                                        <div class="honor-carousel__rank" :class="honorRankClass(student.rank)">
                                            {{ student.rank }}
                                        </div>
                                        <v-avatar size="80" class="honor-carousel__avatar">
                                            <img :src="student.avatar" :alt="student.name" />
                                        </v-avatar>
                                        <div class="honor-carousel__mark">
                                            <v-chip x-small color="primary" class="font-weight-bold">
                                                {{ student.earnedMark }}/{{ student.totalMark }}
                                            </v-chip>
                                        </div>
                                        <div class="honor-carousel__meta text-center">
                                            <div class="honor-carousel__name font-weight-bold strong-text--text">
                                                {{ student.name }}
                                            </div>
                                            <div
                                                v-if="student.governorate"
                                                class="honor-carousel__gov details-text text--text"
                                            >
                                                {{ student.governorate }}
                                            </div>
                                        </div>
                                    </div>
                                </v-slide-item>
                            </v-slide-group>
                        </template>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <v-card v-if="phoneOnly && !homeUiTypeSubjects" flat class="mb-lg-6">
                    <div class="py-2 w-100 px-2">
                        <div v-if="pcOnly" class="px-4">
                            <div style="font-size: 1.3rem" class="strong-text--text mb-1">
                                أهلا بك<span class="mx-1">{{user.name}}</span> مجدداً
                            </div>
                            <p class="medium-text">ابدأ اﻵن بتصفح دوراتك والحصص الجديدة!</p>
                        </div>
                        <v-list v-if="homeMode === 'classic'" :max-width="pcOnly ? 500 : undefined" min-width="300">
                            <v-list-item :dark="phoneOnly" :to="{name: 'my-courses'}" :class="{'secondary md-shadow md-rounded mb-3 mx-3': phoneOnly}">
                                <v-list-item-icon>
                                    <v-icon>mdi-book-multiple-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>دوراتي</v-list-item-title>
                                <v-list-item-icon>
                                    <v-icon style="opacity: 0.5" size="22">mdi-chevron-left</v-icon>
                                </v-list-item-icon>
                            </v-list-item>
                            <v-list-item :dark="phoneOnly" :to="{name: 'courses'}" :class="{'primary md-shadow md-rounded mb-2 mx-3': phoneOnly}">
                                <v-list-item-icon>
                                    <v-icon>mdi-play-box-multiple-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>الدورات التعليمية</v-list-item-title>
                                <v-list-item-icon>
                                    <v-icon style="opacity: 0.5" size="22">mdi-chevron-left</v-icon>
                                </v-list-item-icon>
                            </v-list-item>
                        </v-list>
                        <div v-else-if="homeMode === 'default'" class="px-4 my-4">
                            <v-row>
                                <v-col :cols="i === navItems.length-1 && navItems.length%2 !== 0 ? 12 : 6" v-for="(item, i) in navItems" :key="item.name" class="pa-2">
                                    <v-card flat class="px-3 py-3" :class="{'bordered': !isDark, 'surface2': isDark}" style="border-radius: 10px!important;" :to="{name: item.name}" v-ripple="{class: 'ripple--color'}">
                                        <div class="d-flex flex-column align-center">
                                            <v-icon size="26" :color="item.color">{{item.icon}}</v-icon>
                                            <div class="mt-2">
                                                <div class="strong-text--text details-text">{{item.text}}</div>
                                            </div>
                                        </div>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </div>
                    </div>
                </v-card>
                <!-- <v-divider v-if="phoneOnly"></v-divider> -->

                <v-fade-transition>
                <v-card
                    v-if="showSubjectsSection"
                    flat
                    class="section-card transparent mb-lg-6"
                    :class="{'px-3': phoneOnly}"
                >
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between pt-0 pt-lg-4" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>المواد</div>
                    </v-card-title>
                    <v-subheader v-if="phoneOnly" class="mt-2 px-0">المواد</v-subheader>
                    <v-card-subtitle v-if="pcOnly">اختر المادة التي تريد دراستها:</v-card-subtitle>
                    <v-card-text class="pt-0 py-lg-4 px-0 px-lg-4">
                        <v-row>
                            <v-col
                                v-for="subject in subjects"
                                :key="'subject-' + subject.id"
                                cols="6"
                                md="4"
                                lg="3"
                                class="py-2"
                            >
                                <subject-card :subject="subject" />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <v-fade-transition>
                <v-card
                    v-if="showTeachersSection"
                    flat
                    class="section-card transparent mb-lg-6"
                    :class="{'px-3': phoneOnly}"
                >
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between pt-0 pt-lg-4" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>المعلمين</div>
                        <div v-if="appData.teachers_page">
                            <v-btn color="primary" :to="{name: 'teachers'}" depressed>
                                <v-icon left>mdi-account-tie-outline</v-icon>
                                كل المعلمين
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-card-subtitle v-if="pcOnly">اختر المعلم الذي تريد تصفح دوراته</v-card-subtitle>
                    <v-subheader v-if="phoneOnly" class="mt-2 px-0">المعلمين</v-subheader>
                    <v-card-text class="pt-0 py-lg-4 px-0 px-lg-4">
                        <template v-if="!loading">
                            <v-row v-if="pcOnly">
                                <v-col
                                    v-for="teacher in teachers"
                                    :key="'teacher-' + teacher.id"
                                    md="3"
                                    lg="3"
                                    xl="2"
                                >
                                    <teacher-card :teacher="teacher" />
                                </v-col>
                            </v-row>
                            <v-list v-else class="pa-0">
                                <teacher-list-item
                                    v-for="(teacher, i) in teachers"
                                    :key="'teacher-list-' + teacher.id"
                                    :teacher="teacher"
                                    :divider="i < teachers.length - 1"
                                />
                            </v-list>
                        </template>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <v-fade-transition>
                <v-card
                    v-if="showPackagesSection"
                    flat
                    class="section-card transparent mb-lg-6"
                >
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between pt-0 pt-lg-4" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>الباقات</div>
                    </v-card-title>
                    <v-card-subtitle v-if="pcOnly">اشترك في الباقة المناسبة</v-card-subtitle>
                    <v-subheader v-if="phoneOnly" class="mt-2">الباقات</v-subheader>
                    <v-card-text class="pt-0 py-lg-4 px-0 px-lg-4">
                        <template v-if="!loading">
                            <div v-if="pcOnly">
                                <v-row>
                                    <v-col
                                        cols="12"
                                        md="6"
                                        lg="3"
                                        v-for="(pkg, i) in packagesComputed"
                                        :key="'pkg-' + pkg.id + '-' + i"
                                        class="d-flex py-2"
                                    >
                                        <package-card :pkg="pkg" @updated="onPackageUpdated" />
                                    </v-col>
                                </v-row>
                            </div>
                            <v-list v-else two-line class="pa-0">
                                <package-list-item
                                    v-for="(pkg, i) in packages"
                                    :key="'pkg-list-' + pkg.id + '-' + i"
                                    :pkg="pkg"
                                    @updated="onPackageUpdated"
                                />
                            </v-list>
                        </template>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <v-fade-transition>
                <v-card v-if="featuredCourses && featuredCourses.length" flat class="section-card transparent mb-lg-10">
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between pt-0 pt-lg-4" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>الدورات المقترحة</div>
                        <div v-if="pcOnly">
                            <v-btn color="primary" :to="{name: 'courses'}" depressed>
                                <v-icon color="secondary" left>mdi-play-box-multiple</v-icon>
                                كل الدورات
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-card-subtitle v-if="pcOnly">قائمة الدورات المميزة المقترحة لك</v-card-subtitle>
                    <v-subheader v-if="phoneOnly" class="mt-2">الدورات المقترحة</v-subheader>
                    <v-card-text class="pt-0 py-lg-4 px-0 px-lg-4">
                        <template v-if="!loading">
                            <div v-if="pcOnly">
                                <v-row>
                                    <v-col cols="12" md="6" lg="3" v-for="(course, i) in featuredCoursesComputed" :key="course.id + '-' + i" class="d-flex py-2">
                                        <course-card short :course="course"> </course-card>
                                    </v-col>
                                </v-row>
                            </div>
                            <v-list v-else two-line class="pa-0">
                                <course-list-item v-for="(course, i) in featuredCourses" :key="course.id + '-' + i" :course="course" />
                            </v-list>
                        </template>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <v-fade-transition>
                <v-card v-if="myPackages && myPackages.length" flat class="section-card transparent mb-lg-10">
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between pt-0 pt-lg-4" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>باقاتي</div>
                    </v-card-title>
                    <v-card-subtitle v-if="pcOnly">الباقات اللي اشتركت فيها</v-card-subtitle>
                    <v-subheader v-if="phoneOnly" class="mt-2">باقاتي</v-subheader>
                    <v-card-text class="pt-0 py-lg-4 px-0 px-lg-4">
                        <template v-if="!loading">
                            <div v-if="pcOnly">
                                <v-row>
                                    <v-col cols="12" md="6" lg="3" v-for="(pkg, i) in myPackagesPreview" :key="'mypkg-' + pkg.id + '-' + i" class="d-flex py-2">
                                        <package-card :pkg="pkg" @updated="onPackageUpdated" />
                                    </v-col>
                                </v-row>
                            </div>
                            <v-list v-else two-line class="pa-0">
                                <package-list-item
                                    v-for="(pkg, i) in myPackagesPreview"
                                    :key="'mypkg-list-' + pkg.id + '-' + i"
                                    :pkg="pkg"
                                    @updated="onPackageUpdated"
                                />
                            </v-list>
                            <div v-if="phoneOnly && myPackages.length > myPackagesPreview.length" class="px-4 pb-3">
                                <v-btn color="primary" text block :to="{name: 'my-courses'}">
                                    عرض كل باقاتي
                                    <v-icon small class="ms-1">mdi-chevron-left</v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <v-fade-transition>
                <v-card v-if="myCourses && myCourses.length" flat class="section-card transparent mb-lg-10">
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between pt-0 pt-lg-4" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>دوراتي</div>
                        <div v-if="pcOnly">
                            <v-btn color="primary" :to="{name: 'my-courses'}" depressed>
                                <v-icon color="secondary" left>mdi-book-multiple-outline</v-icon>
                                كل دوراتي
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-card-subtitle v-if="pcOnly">الدورات اللي انضممت لها</v-card-subtitle>
                    <v-subheader v-if="phoneOnly" class="mt-2">دوراتي</v-subheader>
                    <v-card-text class="pt-0 py-lg-4 px-0 px-lg-4">
                        <template v-if="!loading">
                            <div v-if="pcOnly">
                                <v-row>
                                    <v-col cols="12" md="6" lg="3" v-for="(course, i) in myCoursesPreview" :key="'my-' + course.id + '-' + i" class="d-flex py-2">
                                        <course-card short :course="course"> </course-card>
                                    </v-col>
                                </v-row>
                            </div>
                            <v-list v-else two-line class="pa-0">
                                <course-list-item v-for="(course, i) in myCoursesPreview" :key="'my-' + course.id + '-' + i" :course="course" />
                            </v-list>
                            <div v-if="phoneOnly && myCourses.length > myCoursesPreview.length" class="px-4 pb-3">
                                <v-btn color="primary" text block :to="{name: 'my-courses'}">
                                    عرض كل دوراتي
                                    <v-icon small class="ms-1">mdi-chevron-left</v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <v-fade-transition>
                <v-card v-if="latestCourses && latestCourses.length" flat class="section-card mb-4">
                    
                    <v-card-title v-if="pcOnly" class="d-flex justify-space-between" :style="pcOnly ? 'font-size: 1.4rem' : ''">
                        <div>الدورات اﻷحدث</div>
                        <div v-if="pcOnly">
                            <v-btn color="secondary" :to="{name: 'courses'}" depressed>
                                <v-icon left>mdi-play-box-multiple-outline</v-icon>
                                كل الدورات
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-card-subtitle v-if="pcOnly">الدورات الجديدة أو التي تحتوي على محاضرات جديدة</v-card-subtitle>
                    <v-subheader v-if="phoneOnly" class="mt-2">الدورات اﻷحدث</v-subheader>

                    <v-card-text class="pt-0 py-lg-4 px-0 px-lg-4">
                        <template v-if="!loading">
                        <div v-if="pcOnly">
                            <v-row>
                                <v-col cols="12" md="6" lg="3" v-for="(course, i) in latestCoursesComputed" :key="`latest-${course.id}-${i}`" class="d-flex py-2">
                                    <course-card short :course="course"> </course-card>
                                </v-col>
                            </v-row>
                        </div>
                        <v-list v-else two-line class="pa-0">
                            <course-list-item v-for="(course, i) in latestCourses" :key="`latest-${course.id}-${i}`" :course="course" />
                        </v-list>
                        </template>
                    </v-card-text>
                </v-card>
                </v-fade-transition>

                <student-activity-feed
                    v-if="isAuthed && user"
                    :activities="activities"
                    :loading="activitiesLoading"
                />
            </v-container>
        </transition>
    </div>
</template>

<script>
// @ is an alias to /src

import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import { getHonorExamName, rankHonorStudents } from '@/helpers/honor-board'

import CourseCard from '@/components/custom/course-card'
import CourseListItem from '@/components/custom/course-list-item'
import StudentActivityFeed from '@/components/custom/student-activity-feed'
import NotificationsMenu from '@/components/main/navbar/notifications-menu'
import PackageCard from '@/components/custom/package-card'
import PackageListItem from '@/components/custom/package-list-item'
import SubjectCard from '@/components/custom/subject-card'
import TeacherCard from '@/components/custom/teacher-card'
import TeacherListItem from '@/components/custom/teacher-list-item'

const HOME_HONOR_PREVIEW_LIMIT = 10;


export default {
    name: "Home",
    components: {
        CourseCard,
        CourseListItem,
        StudentActivityFeed,
        NotificationsMenu,
        PackageCard,
        PackageListItem,
        SubjectCard,
        TeacherCard,
        TeacherListItem,
    },
    data(){
        return {
            loading: false,
            activitiesLoading: false,
            activities: null,
            honorPanelRemote: null,
            honorPanelRemoteMeta: null,
            honorPanelLoading: false,

            showMoreFeatured: false,
            showMoreLatest: false,
            showMorePackages: false,
            bannerWidth: 0,
        }
    },
    computed: {
        homeData(){
            return this.$store.state.globalModule.homeData;
        },
        headerImages(){
            return this.homeData ? this.homeData.header_images : [];
        },
        packages(){
            return this.homeData && Array.isArray(this.homeData.packages)
                ? this.homeData.packages
                : [];
        },
        packagesComputed(){
            if (this.packages){
                return this.showMorePackages ? this.packages : this.packages.slice(0, 4);
            }
            return [];
        },
        showPackagesSection(){
            return this.packages && this.packages.length > 0;
        },
        homeUiTypeSubjects(){
            return this.appData && this.appData.enable_subjects_system
                && this.appData.home_page_type === 'subjects_ui';
        },
        subjects(){
            return this.homeData && Array.isArray(this.homeData.subjects)
                ? this.homeData.subjects
                : [];
        },
        showSubjectsSection(){
            return this.homeUiTypeSubjects && this.subjects.length > 0;
        },
        teachers(){
            return this.homeData && Array.isArray(this.homeData.teachers)
                ? this.homeData.teachers
                : [];
        },
        showTeachersSection(){
            return this.homeUiTypeSubjects && this.teachers.length > 0;
        },
        myPackages(){
            const fromHome = this.homeData && Array.isArray(this.homeData.my_packages)
                ? this.homeData.my_packages
                : null;
            if (fromHome && fromHome.length) {
                return fromHome;
            }
            return this.$store.state.coursesModule.myPackages || [];
        },
        myPackagesPreview(){
            if (!this.myPackages || !this.myPackages.length) {
                return [];
            }
            return this.myPackages.slice(0, 4);
        },
        myCourses(){
            return this.$store.state.coursesModule.myCourses || [];
        },
        enrolledCourseIds(){
            const ids = {};
            this.myCourses.forEach((course) => {
                if (course && course.id != null) {
                    ids[course.id] = true;
                }
            });
            return ids;
        },
        featuredCourses(){
            const courses = this.homeData ? this.homeData.featured_courses : [];
            if (!courses || !courses.length) {
                return [];
            }
            return courses.filter((course) => !this.isEnrolledCourse(course));
        },
        honorPanelFromHome(){
            return this.homeData && Array.isArray(this.homeData.honor_panel)
                ? this.homeData.honor_panel
                : [];
        },
        honorPanel(){
            // Prefer /home list when present; otherwise use /honor-panel (separated mode)
            if (this.honorPanelFromHome.length) {
                return this.honorPanelFromHome;
            }
            return Array.isArray(this.honorPanelRemote) ? this.honorPanelRemote : [];
        },
        honorPanelPreview(){
            return this.honorPanel.slice(0, HOME_HONOR_PREVIEW_LIMIT);
        },
        rankedHonorPreview(){
            return rankHonorStudents(this.honorPanelPreview);
        },
        homeHonorExamName(){
            if (this.honorPanelFromHome.length) {
                return getHonorExamName(this.homeData, this.honorPanelFromHome);
            }
            return getHonorExamName(this.honorPanelRemoteMeta, this.honorPanel);
        },
        showHomeHonorPanel(){
            // Always show top-10 teaser on Home when honor data is available
            // (home payload or fetched /honor-panel in separated mode)
            const place = this.appData && this.appData.honor_panel_place;
            return place !== 'hidden' && place !== 'none' && place !== false;
        },
        showHonorPanelViewAll(){
            const place = this.appData && this.appData.honor_panel_place;
            return place === 'separated' || this.honorPanel.length > HOME_HONOR_PREVIEW_LIMIT;
        },
        featuredCoursesComputed(){
            if (this.featuredCourses){
                return this.showMoreFeatured ? this.featuredCourses : this.featuredCourses.slice(0, 4);
            }
            return [];
        },
        myCoursesPreview(){
            if (!this.myCourses || !this.myCourses.length) {
                return [];
            }
            return this.myCourses.slice(0, 4);
        },
        latestCoursesComputed(){
            if (this.latestCourses){
                return this.showMoreLatest ? this.latestCourses : this.latestCourses.slice(0, 4);
            }
            return [];
        },
        latestCourses(){
            return this.homeData ? this.homeData.latest_courses : [];
        },
        bannerCarouselHeight(){
            const width = this.bannerWidth || this.fallbackBannerWidth;
            const aspectRatio = 2.75;
            let height = Math.round(width / aspectRatio);

            if (this.$vuetify.breakpoint.xs) {
                height = Math.min(Math.max(height, 132), 200);
            } else if (this.$vuetify.breakpoint.sm) {
                height = Math.min(Math.max(height, 168), 240);
            } else if (this.$vuetify.breakpoint.md) {
                height = Math.min(Math.max(height, 220), 320);
            } else if (this.$vuetify.breakpoint.lg) {
                height = Math.min(Math.max(height, 260), 380);
            } else {
                height = Math.min(Math.max(height, 280), 420);
            }

            return height;
        },
        fallbackBannerWidth(){
            if (typeof window === 'undefined') {
                return 360;
            }

            const viewport = window.innerWidth;
            if (this.phoneOnly) {
                return Math.max(viewport - 24, 280);
            }

            return Math.min(viewport - 48, 1280);
        },
        homeMode(){
            return this.appData.home_mode || 'default';
        },
        navItems(){
            let items = [
                {name: 'my-courses', text: 'دوراتي', icon: 'mdi-book-multiple-outline', color: 'primary', foreground: 'primary lighten-5'},
                {name: 'courses', text: 'الدورات التعليمية', icon: 'mdi-play-box-multiple-outline', color: 'primary', foreground: 'primary lighten-5'},
                {name: 'attachments', text: 'مذكرات', icon: 'mdi-bookshelf', color: 'primary', foreground: 'primary lighten-5'},

                {name: 'profile', text: 'الملف الشخصي', icon: 'mdi-account-circle-outline', color: 'strong-text', foreground: 'green lighten-5'},
                {name: 'teachers', text: 'المعلمين', icon: 'mdi-account-tie-outline', color: 'strong-text', foreground: 'grey lighten-3', hide: !this.appData.teachers_page},
                {name: 'honor-panel', text: 'لوحة الشرف', icon: 'mdi-trophy-outline', color: 'amber darken-3', foreground: 'amber lighten-5', hide: !this.showHonorPanelViewAll},
                {name: 'exams', text: 'الامتحان اﻹلكتروني', icon: 'mdi-clipboard-text-outline', color: 'deep-purple darken-1', foreground: 'deep-purple lighten-5', hide: !this.appData.enable_gen_exm},
                {name: 'my-exams', text: 'نتائج امتحاناتي', icon: 'mdi-license', color: 'deep-purple darken-1', foreground: 'deep-purple lighten-5'},
                {name: 'my-homeworks', text: 'نتائج واجباتي', icon: 'mdi-license', color: 'deep-purple darken-1', foreground: 'deep-purple lighten-5'},
                {name: 'wrong-answers', text: 'سجل الإجابات الخاطئة', icon: 'mdi-clipboard-remove-outline', color: 'deep-purple darken-1', foreground: 'deep-purple lighten-5', hide: !this.appData.enable_wrong_answers_page},
                {name: 'my-questions', text: 'أسئلتي', icon: 'mdi-file-question-outline', color: 'primary', foreground: 'deep-purple lighten-5'},
                {name: 'questions-bank', text: 'بنك الأسئلة', icon: 'mdi-folder-multiple-outline', color: 'primary', foreground: 'deep-purple lighten-5'},
                {name: 'wallet', text: 'المحفظة الإلكترونية', icon: 'mdi-wallet-outline', color: 'deep-orange darken-1', foreground: 'orange lighten-5', hide: !this.appData.enable_wallet_system},
                {name: 'center-wallet', text: 'مدفوعات السنتر', icon: 'mdi-wallet-bifold-outline', color: 'teal darken-1', foreground: 'teal lighten-5', hide: !this.appData.enable_student_center_wallet_section || !this.user.center_id},
                {name: 'links', text: 'روابطنا الرسمية', icon: 'mdi-link', color: 'primary', foreground: 'grey lighten-5'},
                {name: 'help', text: 'الدعم الفني', icon: 'mdi-help-circle-outline', color: 'strong-text', foreground: 'grey lighten-3'},
            ];
            return items.filter(item=>{return !item.hide});
        },
        ...GlobalComputed
    },
    methods: {
        ...GlobalMethods,
        loadData(){
            if (!this.loading && this.homeData.featured_courses === null){
                this.loading = true;

                Promise.all([
                    this.$store.dispatch('loadHomeData'),
                    this.loadActivities(),
                ]).then(()=>{
                    this.loading = false;
                    this.loadHonorPanelPreview();
                }).catch(()=>{
                    this.showMsg('حدثت مشكلة أثناء تحميل البيانات');
                    this.loading = false;
                    this.loadHonorPanelPreview();
                });
            } else {
                this.loadActivities();
                this.loadHonorPanelPreview();
            }
        },
        loadHonorPanelPreview(){
            if (!this.showHomeHonorPanel || this.honorPanelLoading) {
                return Promise.resolve(this.honorPanel);
            }

            // /home already has honor students — no extra request needed
            if (this.honorPanelFromHome.length) {
                return Promise.resolve(this.honorPanelFromHome);
            }

            // Separated mode (or empty home list): fetch full panel and show top 10
            this.honorPanelLoading = true;
            return this.$store.dispatch('fastGetAction', { url: '/honor-panel' }).then((response) => {
                const data = (response && response.data) || {};
                this.honorPanelRemote = data.honor_panel || [];
                this.honorPanelRemoteMeta = data;
                this.honorPanelLoading = false;
                return this.honorPanelRemote;
            }).catch(() => {
                this.honorPanelRemote = [];
                this.honorPanelRemoteMeta = null;
                this.honorPanelLoading = false;
                return [];
            });
        },
        loadActivities(){
            if (this.activitiesLoading){
                return Promise.resolve(this.activities || []);
            }

            this.activitiesLoading = true;
            return this.$store.dispatch('fetchStudentActivities')
                .then((activities)=>{
                    this.activities = activities;
                    this.activitiesLoading = false;
                    return activities;
                })
                .catch(()=>{
                    this.activities = [];
                    this.activitiesLoading = false;
                    return [];
                });
        },
        onPackageUpdated(){
            this.$store.dispatch('loadHomeData').catch(()=>{});
            this.$store.dispatch('getMyCourses').catch(()=>{});
        },
        isEnrolledCourse(course){
            if (!course || course.id == null) {
                return false;
            }
            return !!this.enrolledCourseIds[course.id] || course.joined === true;
        },
        honorRankClass(rank){
            if (rank === 1) return 'honor-carousel__rank--gold';
            if (rank === 2) return 'honor-carousel__rank--silver';
            if (rank === 3) return 'honor-carousel__rank--bronze';
            return 'honor-carousel__rank--default';
        },
        imageClickAction(img){
            if (img.href){
                window.open(img.href, "_system");
            }else if (img.to){
                this.$router.push(img.to);
            }
        },
        updateBannerWidth(){
            const wrap = this.$refs.bannerCarouselWrap;
            if (wrap) {
                this.bannerWidth = wrap.clientWidth;
            }
        },
        onBannerResize(){
            this.updateBannerWidth();
        },

        checkAppAutoLogin(){
            if (!this.capRunning && this.phoneAndTablet && this.appData.enable_wallet_system){
                let tk = this.$route.query.tk;
                if (tk && this.$route.query.from === 'app' && this.$route.query.to === 'w'){
                    tk = tk.split('').reverse().join('');
                    // this.$router.push({query: {}});
                    window.localStorage.setItem('tK', tk);
                    // this.$router.push({name: 'wallet'});
                    let redirectTo = '/wallet';
                    if (this.$route.query.q){
                        redirectTo += '?';
                        redirectTo += this.$route.query.q.replaceAll(':', '=').replaceAll('|', '&');
                    }
                    window.location = redirectTo;
                }
            }
        }
    },
    watch: {
        loading(loading){
            this.setLinearLoading(loading);
        },
        headerImages: {
            handler(images) {
                if (images && images.length) {
                    this.$nextTick(this.updateBannerWidth);
                }
            },
            immediate: true,
        },
    },
    created(){
        if (this.$route.query.course){
            this.$router.push({name: 'course', params: {id: this.$route.query.course}});
        }
        this.checkAppAutoLogin();
    },
    mounted(){
        this.$nextTick(this.updateBannerWidth);
        window.addEventListener('resize', this.onBannerResize);

        if (!this.latestCourses || this.latestCourses.length === 0 || this.homeData.featured_courses === null){
            this.loadData();
        } else {
            this.loadActivities();
            this.loadHonorPanelPreview();
        }
    },
    beforeDestroy(){
        window.removeEventListener('resize', this.onBannerResize);
    }
    
};
</script>

<style lang="scss" scoped>
    @media (min-width: 756px) {
        .v-card.section-card{
            .v-card__title, .v-card__subtitle, .v-card__text{
                padding-left: 0!important;
                padding-right: 0!important;
            }
        }
    }
    .header-carousel-wrap {
        width: 100%;
        max-width: 100%;
        margin-top: 0.75rem;
        padding-left: 12px;
        padding-right: 12px;
        box-sizing: border-box;

        @media (min-width: 960px) {
            margin-top: 0;
            padding-left: 0;
            padding-right: 0;
        }
    }

    .honor-section {
        overflow: visible !important;
    }

    .honor-section__mobile-head {
        position: relative;
        z-index: 1;
    }

    .honor-section__subheader {
        font-size: 0.95rem;
        font-weight: 800;
        line-height: 1.3;
    }

    .honor-section__hint {
        font-size: 0.72rem;
        white-space: nowrap;
    }

    .honor-section__body {
        overflow: visible !important;
    }

    .honor-scroll-outer {
        width: 100%;
        max-width: 100vw;
        overflow: visible;
    }

    .honor-scroll {
        display: flex;
        flex-flow: row nowrap;
        align-items: stretch;
        gap: 0.5rem;
        width: 100%;
        max-width: 100%;
        overflow-x: scroll;
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior-x: contain;
        touch-action: pan-x;
        padding: 0.5rem 1rem 0.75rem;
        padding-inline-end: 2rem;
        box-sizing: border-box;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
            display: none;
            height: 0;
            width: 0;
        }
    }

    .honor-carousel__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 0 0 auto;
        width: 6.35rem;
        min-width: 6.35rem;
        min-height: 10.25rem;
        margin: 0;
        padding: 0.55rem 0.35rem 0.45rem;
        box-sizing: border-box;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid rgba(0, 0, 0, 0.05);

        &--desktop {
            width: 8.5rem;
            min-width: 8.5rem;
            min-height: 11rem;
            margin-inline-end: 1rem;
            background: transparent;
            border: 0;
            padding: 0.2rem 0.25rem 0.35rem;
        }
    }

    .honor-carousel__rank {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.55rem;
        height: 1.55rem;
        margin-bottom: 0.35rem;
        padding: 0 0.35rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 800;
        line-height: 1;

        &--gold {
            background: linear-gradient(135deg, #f6d365, #fda085);
            color: #5b3b00;
        }

        &--silver {
            background: linear-gradient(135deg, #e8e8e8, #c0c0c0);
            color: #333;
        }

        &--bronze {
            background: linear-gradient(135deg, #f0c27b, #c68642);
            color: #4a2c0a;
        }

        &--default {
            background: rgba(0, 0, 0, 0.08);
            color: inherit;
        }
    }

    .honor-carousel__avatar {
        flex-shrink: 0;
    }

    .honor-carousel__mark {
        margin-top: -10px;
        z-index: 1;
        flex-shrink: 0;
    }

    .honor-carousel__meta {
        margin-top: 0.35rem;
        width: 100%;
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    .honor-carousel__name {
        font-size: 0.78rem;
        line-height: 1.35;
        width: 100%;
        white-space: normal;
        overflow: visible;
        word-break: break-word;
        overflow-wrap: anywhere;
    }

    .honor-carousel__gov {
        margin-top: 0.2rem;
        font-size: 0.7rem;
        line-height: 1.3;
        width: 100%;
        white-space: normal;
        overflow: visible;
        opacity: 0.85;
    }

    .honor-carousel {
        ::v-deep .v-slide-group__content {
            align-items: flex-start;
            padding-bottom: 0.25rem;
        }

        ::v-deep .v-slide-group__wrapper {
            contain: none;
        }
    }

    .header-carousel{
        width: 100%;
        border-radius: 12px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        overflow: hidden;
        background: #eef1f6;

        @media(min-width: 960px){
            border-radius: 10px;
        }
    }

    .header-carousel__slide {
        width: 100%;
        height: 100%;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-color: #eef1f6;
    }
    .welcome-card{
        background: linear-gradient(-25deg, #332575, #723fe0)!important;
    }
</style>
<style lang="scss">
.home-page .honor-section.v-card {
    overflow: visible !important;
}

.home-page .honor-section .v-card__text {
    overflow: visible !important;
}

.home-page .honor-scroll {
    /* Android WebView: keep horizontal pan free of parent vertical-scroll greed */
    will-change: scroll-position;
}

.v-window__prev, .v-window__next{
    background: none!important;
}
.v-carousel__controls{
    background: none!important;
    .v-carousel__controls__item{
        margin: 0 2px!important;

        .v-icon.mdi-circle{
            font-size: 15px!important;
        }
    }
}
.v-carousel__controls .v-carousel__controls__item{
    margin: 0!important;
}
.v-carousel__controls .v-carousel__controls__item .v-icon.mdi-circle{
    font-size: 10px!important;
}
.v-carousel__controls .v-carousel__controls__item.v-btn--active .v-icon.mdi-circle{
    font-size: 14px!important;
}
</style>
